import { z } from 'zod';

import { dayjs } from '@/utilities';

export const CALENDAR_EVENT_SOURCE_IDS = {
    dividend: 'dividend',
    economic: 'economic',
} as const;

export type CalendarEventSourceId = (typeof CALENDAR_EVENT_SOURCE_IDS)[keyof typeof CALENDAR_EVENT_SOURCE_IDS];

export type CalendarEventTone = 'blue' | 'gold';
export type EconomicEventImpact = 'high' | 'low' | 'medium' | 'unknown';

interface BaseCalendarEvent {
    date: string;
    id: string;
    source: CalendarEventSourceId;
    startsAt: string;
    title: string;
}

export interface DividendCalendarEvent extends BaseCalendarEvent {
    adjustedDividend?: number;
    declarationDate?: string;
    dividend?: number;
    label?: string;
    paymentDate?: string;
    recordDate?: string;
    source: typeof CALENDAR_EVENT_SOURCE_IDS.dividend;
    symbol: string;
}

export interface EconomicCalendarEvent extends BaseCalendarEvent {
    actual?: number;
    change?: number;
    changePercentage?: number;
    country?: string;
    currency?: string;
    estimate?: number;
    impact: EconomicEventImpact;
    previous?: number;
    source: typeof CALENDAR_EVENT_SOURCE_IDS.economic;
    unit?: string;
}

export type MarketCalendarEvent = DividendCalendarEvent | EconomicCalendarEvent;

export interface CalendarSelection {
    date: string;
    events: MarketCalendarEvent[];
}

export const displayCalendarEventNumber = (value: number | undefined, unit?: string): string => {
    if (value === undefined) {
        return '—';
    }

    const formattedValue = new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 4,
    }).format(value);

    return unit ? `${formattedValue}${unit}` : formattedValue;
};

export interface CalendarEventSourceDefinition {
    endpoint: string;
    id: CalendarEventSourceId;
    label: string;
    normalize: (payload: unknown) => MarketCalendarEvent[];
    tone: CalendarEventTone;
}

const optionalScalarSchema = z.union([z.number(), z.string()]).nullish();

const dividendSchema = z
    .object({
        adjDividend: optionalScalarSchema,
        date: z.string().trim().min(1),
        declarationDate: z.string().trim().nullish(),
        dividend: optionalScalarSchema,
        label: z.string().trim().nullish(),
        paymentDate: z.string().trim().nullish(),
        recordDate: z.string().trim().nullish(),
        symbol: z.string().trim().min(1),
    })
    .passthrough();

const economicEventSchema = z
    .object({
        actual: optionalScalarSchema,
        change: optionalScalarSchema,
        changePercentage: optionalScalarSchema,
        country: z.string().trim().nullish(),
        currency: z.string().trim().nullish(),
        date: z.string().trim().min(1),
        estimate: optionalScalarSchema,
        event: z.string().trim().min(1),
        impact: z.string().trim().nullish(),
        previous: optionalScalarSchema,
        unit: z.string().trim().nullish(),
    })
    .passthrough();

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const payloadRows = (payload: unknown): unknown[] => {
    if (Array.isArray(payload)) {
        return payload;
    }
    if (isRecord(payload) && Array.isArray(payload.data)) {
        return payload.data;
    }

    return [];
};

const normalizeDate = (value: string): string | undefined => {
    const datePrefix = value.match(/^\d{4}-\d{2}-\d{2}/)?.[0];

    if (datePrefix && dayjs(datePrefix, 'YYYY-MM-DD', true).isValid()) {
        return datePrefix;
    }

    const parsedDate = dayjs(value);
    return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : undefined;
};

const normalizeNumber = (value: number | string | null | undefined): number | undefined => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value !== 'string' || value.trim().length === 0) {
        return undefined;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const normalizeOptionalString = (value: string | null | undefined): string | undefined =>
    value && value.trim().length > 0 ? value : undefined;

const normalizeImpact = (value: string | null | undefined): EconomicEventImpact => {
    const normalizedImpact = value?.trim().toLocaleLowerCase();

    if (normalizedImpact === 'high' || normalizedImpact === 'low' || normalizedImpact === 'medium') {
        return normalizedImpact;
    }

    return 'unknown';
};

export const normalizeDividendEvents = (payload: unknown): DividendCalendarEvent[] =>
    payloadRows(payload).flatMap((row, index) => {
        const result = dividendSchema.safeParse(row);
        if (!result.success) {
            return [];
        }

        const date = normalizeDate(result.data.date);
        if (!date) {
            return [];
        }

        const symbol = result.data.symbol.toLocaleUpperCase();

        return [
            {
                adjustedDividend: normalizeNumber(result.data.adjDividend),
                date,
                declarationDate: normalizeOptionalString(result.data.declarationDate),
                dividend: normalizeNumber(result.data.dividend),
                id: `dividend:${date}:${symbol}:${result.data.recordDate ?? index}`,
                label: normalizeOptionalString(result.data.label),
                paymentDate: normalizeOptionalString(result.data.paymentDate),
                recordDate: normalizeOptionalString(result.data.recordDate),
                source: CALENDAR_EVENT_SOURCE_IDS.dividend,
                startsAt: result.data.date,
                symbol,
                title: `${symbol} dividend`,
            },
        ];
    });

export const normalizeEconomicEvents = (payload: unknown): EconomicCalendarEvent[] =>
    payloadRows(payload).flatMap((row, index) => {
        const result = economicEventSchema.safeParse(row);
        if (!result.success) {
            return [];
        }

        const date = normalizeDate(result.data.date);
        if (!date) {
            return [];
        }

        const country = normalizeOptionalString(result.data.country)?.toLocaleUpperCase();
        const currency = normalizeOptionalString(result.data.currency)?.toLocaleUpperCase();

        return [
            {
                actual: normalizeNumber(result.data.actual),
                change: normalizeNumber(result.data.change),
                changePercentage: normalizeNumber(result.data.changePercentage),
                country,
                currency,
                date,
                estimate: normalizeNumber(result.data.estimate),
                id: `economic:${date}:${country ?? currency ?? 'global'}:${result.data.event}:${index}`,
                impact: normalizeImpact(result.data.impact),
                previous: normalizeNumber(result.data.previous),
                source: CALENDAR_EVENT_SOURCE_IDS.economic,
                startsAt: result.data.date,
                title: result.data.event,
                unit: normalizeOptionalString(result.data.unit),
            },
        ];
    });

export const CALENDAR_EVENT_SOURCES: readonly CalendarEventSourceDefinition[] = [
    {
        endpoint: '/api/vendors/fmp/calendars/dividends',
        id: CALENDAR_EVENT_SOURCE_IDS.dividend,
        label: 'Dividends',
        normalize: normalizeDividendEvents,
        tone: 'gold',
    },
    {
        endpoint: '/api/vendors/fmp/calendars/economic',
        id: CALENDAR_EVENT_SOURCE_IDS.economic,
        label: 'Economic events',
        normalize: normalizeEconomicEvents,
        tone: 'blue',
    },
];
